import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { CARD } from "@/components/home/shared";

export const IndexAlertsCard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["index-alerts"],
    queryFn: async () => (await api.get("/account/alerts")).data,
    enabled: !!user,
  });
  const prefs = data?.index_flip || { push: true, email: false };

  const save = async (patch: Partial<{ push: boolean; email: boolean }>) => {
    try {
      await api.post("/account/alerts", { index_flip: { ...prefs, ...patch } });
      queryClient.invalidateQueries({ queryKey: ["index-alerts"] });
      toast.success("Alert preferences saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className={`${CARD} p-4 mt-5`} data-testid="index-alerts-card">
      <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A78BFA]">
        <BellRing className="h-3.5 w-3.5 text-[#00E5FF]" /> Index Alerts
      </p>
      <p className="text-[13px] text-gray-400 leading-snug mt-2">
        Get notified the moment the index flips between fear and greed.
      </p>
      {user ? (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-300" data-testid="index-alerts-push">
              <Switch checked={!!prefs.push} onCheckedChange={(v: any) => save({ push: !!v })} />
              Push
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300" data-testid="index-alerts-email">
              <Switch checked={!!prefs.email} onCheckedChange={(v: any) => save({ email: !!v })} />
              Email
            </label>
          </div>
          {prefs.push && (
            <p className="text-[11px] text-gray-600 mt-2.5">
              Push arrives as a browser notification — enable notifications via the bell icon in the header.
            </p>
          )}
        </>
      ) : (
        <Link to="/login" data-testid="index-alerts-signin"
          className="inline-block mt-3 text-[13px] font-bold text-[#00E5FF] hover:text-white transition-colors">
          Sign in to enable alerts →
        </Link>
      )}
    </div>
  );
};
